import {
  Button,
  ButtonColor,
  IconChevronDown,
  IconDotsVertical,
  Tooltip,
} from '@leland-dev/leland-ui-library';
import React, { type SVGProps, useEffect, useRef, useState } from 'react';

export interface CustomButtonProps {
  onClick?: () => void;
  isMenuOpen: boolean;
}

type DropdownProps =
  | {
      dropDownView: boolean;
      dropDownClassName?: string;
    }
  | {
      dropDownView?: false;
      dropDownClassName?: never;
    };

type ActionsMenuProps = DropdownProps & {
  actions: ActionMenuItemProps[];
  selectedOption?: string | null;
  className?: string;
  CustomButton?: React.FC<CustomButtonProps>;
  rounded?: boolean;
  closeOnItemClick?: boolean;
  label?: string;
  dropdownAlignment?: DropdownAlignment;
  inline?: boolean;
  Icon?: React.FC<SVGProps<SVGSVGElement>>;
};

export enum DropdownAlignment {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({
  actions,
  selectedOption,
  dropDownView,
  className = '',
  dropDownClassName = '',
  CustomButton,
  closeOnItemClick,
  rounded,
  label,
  dropdownAlignment = DropdownAlignment.RIGHT,
  inline = false,
  Icon,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedOptionLabel, setSelectedOptionLabel] = useState(
    selectedOption ?? null,
  );

  useEffect(() => {
    if (isMenuOpen) {
      const handleClick = (e: MouseEvent) => {
        if (menuRef.current?.contains(e.target as HTMLElement)) {
          return;
        }
        setIsMenuOpen(false);
      };

      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [isMenuOpen]);

  return (
    <div
      ref={menuRef}
      className={`relative items-center ${inline ? 'inline-flex' : 'flex'}`}
    >
      {dropDownView ? (
        <button
          type="button"
          className={`flex items-center justify-center rounded-lg border px-3 py-2 text-sm text-leland-gray-dark ${className}`}
          onClick={() => setIsMenuOpen((prev) => !prev)}
        >
          <div className="flex items-center justify-center font-medium">
            {selectedOptionLabel}
            <div
              className={`ml-1.5 self-center leading-none text-gray-400 transition-transform ${
                isMenuOpen && 'rotate-180'
              }`}
            >
              <IconChevronDown className="size-5 text-leland-gray-dark" />
            </div>
          </div>
        </button>
      ) : CustomButton ? (
        <CustomButton
          onClick={() => setIsMenuOpen((prev) => !prev)}
          isMenuOpen={isMenuOpen}
        />
      ) : label ? (
        <Button
          label={label}
          RightIcon={IconChevronDown}
          LeftIcon={Icon}
          buttonColor={ButtonColor.WHITE}
          rounded={rounded}
          onClick={() => setIsMenuOpen((prev) => !prev)}
        />
      ) : (
        <Button
          label="Actions"
          hideLabel
          LeftIcon={IconDotsVertical}
          buttonColor={ButtonColor.WHITE}
          rounded={rounded}
          onClick={() => setIsMenuOpen((prev) => !prev)}
        />
      )}

      {isMenuOpen ? (
        <div
          className={`absolute top-full z-10 mt-2 flex min-w-48 origin-top-right flex-col items-start self-start rounded-md bg-white p-2 shadow-lg ring-1 ring-black/5 ${dropDownClassName} ${dropdownAlignment === DropdownAlignment.RIGHT ? 'right-0' : 'left-0'}`}
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="article-options"
        >
          {actions.map((action, index) => {
            return (
              <ActionMenuItem
                key={index}
                Icon={action.Icon}
                label={action.label}
                onClick={() => {
                  setIsMenuOpen(false);
                  action.onClick();
                  setSelectedOptionLabel(action.label);
                  if (closeOnItemClick) {
                    setIsMenuOpen(false);
                  }
                }}
                tooltipMessage={action.tooltipMessage}
                disabled={action.disabled}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export interface ActionMenuItemProps {
  disabled?: boolean;
  onClick: () => void;
  label: string;
  Icon?: React.FC<SVGProps<SVGSVGElement>>;
  tooltipMessage?: string | null;
}

const ActionMenuItem: React.FC<ActionMenuItemProps> = ({
  disabled,
  onClick,
  label,
  Icon,
  tooltipMessage,
}) => {
  const button = (
    <button
      className="button flex w-full cursor-pointer items-center justify-between rounded-md px-3.5 py-3 hover:bg-leland-gray-hover active:bg-leland-gray-hover"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      <div className="flex items-center justify-start whitespace-nowrap text-left text-base font-medium text-leland-gray-dark">
        {Icon ? <Icon className="mr-2 size-5 text-leland-gray-dark" /> : null}
        {label}
      </div>
    </button>
  );

  return tooltipMessage ? (
    <Tooltip content={tooltipMessage}>{button}</Tooltip>
  ) : (
    button
  );
};

export default ActionsMenu;
